import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import Loading from '../components/Loading';

const AuthContext = createContext(null);

const emptyProfile = { memberships: [], isPlatformAdmin: false, loaded: false, userId: null };

/**
 * Holds the signed-in session plus what the user belongs to:
 * memberships = [{ role, status, org_id, organizations: { id, name, slug, kind, status } }]
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined); // undefined = still loading
  const [profile, setProfile] = useState(emptyProfile);

  const loadProfile = useCallback(async (user) => {
    if (!user) {
      setProfile({ ...emptyProfile, loaded: true, userId: null });
      return;
    }
    const [members, admin] = await Promise.all([
      supabase
        .from('org_members')
        .select('role, status, org_id, organizations(id, name, slug, kind, status, plan, billing_status, comp_until, offer, stripe_customer_id, stripe_subscription_id)')
        .eq('user_id', user.id),
      supabase.from('platform_admins').select('user_id').eq('user_id', user.id).maybeSingle(),
    ]);
    if (members.error) console.error('Error loading memberships:', members.error);
    setProfile({
      memberships: (members.data || []).filter((m) => m.organizations),
      isPlatformAdmin: !!admin.data,
      loaded: true,
      userId: user.id,
    });
  }, []);

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      // Defer queries out of the auth callback (supabase-js can deadlock otherwise)
      setTimeout(() => loadProfile(newSession?.user ?? null), 0);
    });
    return () => data.subscription.unsubscribe();
  }, [loadProfile]);

  const refreshProfile = useCallback(
    () => loadProfile(session?.user ?? null),
    [loadProfile, session]
  );

  const value = {
    session,
    user: session?.user ?? null,
    ...profile,
    // Loaded only for the user signed in now: right after sign-in the signed-out profile
    // (loaded, no memberships) must not count, or the dashboard flashes its first-run screen
    loaded: profile.loaded && profile.userId === (session?.user?.id ?? null),
    refreshProfile,
    signOut: () => supabase.auth.signOut(),
    /** owner/admin of the org, or a platform admin */
    canManageOrg: (orgId) =>
      profile.isPlatformAdmin ||
      profile.memberships.some(
        (m) => m.org_id === orgId && m.status === 'active' && ['owner', 'admin'].includes(m.role)
      ),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Sends signed-out visitors to /login and brings them back afterwards. */
export function RequireAuth({ children, platformAdmin = false }) {
  const { session, loaded, isPlatformAdmin } = useAuth();
  const location = useLocation();

  if (session === undefined || (session && !loaded)) return <Loading />;
  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (platformAdmin && !isPlatformAdmin) return <Navigate to="/" replace />;
  return children;
}
